/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable sonarjs/no-duplicate-string */

import {
  takeEvery, put, call, select,
} from 'redux-saga/effects';

import {
  MODALS, API_URLS, SUCCESS_MODALS, FETCH_OPTIONS,
} from '../constants';
import { requestGet, requestPost } from '../utils/request';
import {
  getWeekSummary, getIntervals, getEditedDays,
  getTennisEditedDays, getTennisIntervals, getTennisWeekSummary,
} from '../selectors/autobooking';
import { aAddInterval, aAddTennisInterval, aGetAutoBooking, aGetTennisAutoBooking, aSaveChanges, aSaveTennisChanges } from '../reducers';
import { aAddIntervalSuccess, aSetIsFetching, aSetOpenModal } from '../reducers/common';
import { aAddIntervalError, aChangeIntervalsSuccess, aChangeTennisIntervalsSuccess, aGetAutobookingSuccess, aGetTennisAutobookingSuccess, aSaveChangesSuccess, aSaveTennisChangesSuccess } from '../reducers/autobooking';

/**
 * @param {object} weekSummary
 * @returns {weekSummary}
 */
const filteredWeekSummary = (weekSummary) => {
  const newWeekSummary = weekSummary;
  const weekSummaryKeys = Object.keys(weekSummary);
  for (let i = 0; i < weekSummaryKeys.length; i += 1) {
    newWeekSummary[weekSummaryKeys[i]] = weekSummary[weekSummaryKeys[i]]
      .filter((interval) => interval.start !== interval.end);
  }
  return newWeekSummary;
};

const filteredTennisWeekSummary = (weekSummary) => {
  const newWeekSummary = { ...weekSummary };
  const weekSummaryKeys = Object.keys(weekSummary);
  for (let i = 0; i < weekSummaryKeys.length; i += 1) {
    const weekSummaryItem = [];
    weekSummary[weekSummaryKeys[i]].forEach((interval) => {
      if (interval.startTime !== interval.endTime) {
        weekSummaryItem.push({
          count: interval.matchesCounter,
          end: interval.endTime,
          id: interval.id,
          start: interval.startTime,
        });
      }
    });
    newWeekSummary[weekSummaryKeys[i]] = weekSummaryItem;
  }
  return newWeekSummary;
};

/**
 * @namespace sagas/autobooking
 */

/**
 * @memberof sagas/autobooking
 * @async
 */
function* getIntervalsSaga() {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_INTERVALS,
      value: true,
    }));
    response = yield call(requestGet, API_URLS.GET_INTERVALS, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      const weekSummary = filteredWeekSummary(response.data.weekSummary);

      yield put(aGetAutobookingSuccess({
        weekSummary,
        intervals: response.data.intervals,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_INTERVALS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_INTERVALS,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/tennisautobooking
 * @async
 */
function* getTennisIntervalsSaga() {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_TENNIS_INTERVALS,
      value: true,
    }));
    const response = yield call(requestGet, API_URLS.GET_TENNIS_INTERVALS, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      const weekSummary = filteredTennisWeekSummary(response.data.weekSummary);
      yield put(aGetTennisAutobookingSuccess({
        weekSummary,
        intervals: response.data.intervals,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_TENNIS_INTERVALS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_TENNIS_INTERVALS,
      value: false,
    }));
  }
}

/**
 * @param {object} interval
 * @param {string} field1
 * @param {string} field2
 * @returns {Date}
 */
function getDateObject(interval, field1, field2) {
  const date = new Date();
  const daytoset = interval[field1];
  const currentDay = date.getDay();
  const distance = daytoset - currentDay;
  date.setDate(date.getDate() + distance);
  const time = interval[field2].split(':');
  date.setHours(time[0], time[1], 0);

  return date.getTime();
}

/**
 * @param {Array} events
 * @returns {Array}
 */
function calculateTotalMatches(events) {
  return events.reduce((count, league) => count + parseInt(league.numMatchesScheduled), 0);
}

/**
 * @param {Array} intervals
 * @param {Date} newStartDate
 * @param {Date} newEndDate
 * @param {string} intervalId
 * @returns {boolean}
 */
function isOverlappingIntervals(intervals, newStartDate, newEndDate, intervalId) {
  let isOverlap = false;
  intervals.map((interval) => {
    const startDate = getDateObject(interval, 'startDay', 'startTime');
    const endDate = getDateObject(interval, 'endDay', 'endTime');

    if (((newStartDate === startDate && newEndDate === endDate)
      || ((newEndDate > startDate) && (newStartDate < endDate)))
      && intervalId !== interval.intervalId) {
      isOverlap = true;
    }
  });
  return isOverlap;
}

/**
 * @param {object} newInterval
 * @param {object} weekSummary
 * @returns {Array}
 */
function getNewWeekSummary(newInterval, weekSummary) {
  const newWeekSummary = structuredClone(weekSummary);
  const startDay = parseInt(newInterval.startDay);
  const endDay = parseInt(newInterval.endDay);
  if (startDay !== endDay) {
    newWeekSummary[startDay].push({
      start: newInterval.startTime,
      end: '23:59',
      id: newInterval.intervalId,
      count: calculateTotalMatches(newInterval.events),
    });

    for (let i = startDay + 1; i < endDay; i += 1) {
      newWeekSummary[i].push({
        start: '00:00',
        end: '23:59',
        id: newInterval.intervalId,
        count: calculateTotalMatches(newInterval.events),
      });
    }
    newWeekSummary[endDay].unshift({
      start: '00:00',
      end: newInterval.endTime,
      id: newInterval.intervalId,
      count: calculateTotalMatches(newInterval.events),
    });
  } else {
    let index = null;

    const dayIntervals = weekSummary[startDay];
    for (let i = 0; i < dayIntervals.length; i += 1) {
      if (dayIntervals[i].start > newInterval.startTime && !index) {
        index = i;
        break;
      }
    }

    const newSummary = {
      start: newInterval.startTime,
      end: newInterval.endTime,
      id: newInterval.intervalId,
      count: calculateTotalMatches(newInterval.events),
    };
    if (index != null) {
      newWeekSummary[startDay].splice(index, 0, newSummary);
    } else {
      newWeekSummary[startDay].push(newSummary);
    }
  }
  return newWeekSummary;
}

/**
 * @param {object} newInterval
 * @param {Array} editedDays
 *  @returns {Array}
 */
function getNewEditedDays(newInterval, editedDays) {
  const newEditedDays = [...editedDays];
  const startDay = parseInt(newInterval.startDay);
  const endDay = parseInt(newInterval.endDay);
  newEditedDays.push(startDay);
  newEditedDays.push(endDay);
  for (let i = startDay + 1; i < endDay; i += 1) {
    newEditedDays.push(i);
  }
  return newEditedDays;
}

/**
 * @memberof sagas/autobooking
 * @async
 * @param {object} action
 */
function* addInterval(action) {
  try {
    const { events } = action.payload;
    if (events.length === 0) {
      throw Error('Please select one league');
    }
    events.forEach((event) => {
      if ((!event.randomMatches && event.preferredTeams === undefined)
      || ((!event.preferredTeams || event.preferredTeams.length === 0) && !event.randomMatches)) {
        throw Error('Please select team preference');
      }
    });

    let weekSummary = {...yield select(getWeekSummary)};

    let intervals = yield select(getIntervals);
    // editing intervals
    if (action.payload.intervalId) {
      intervals = intervals.filter((x) => (
        parseInt(x.intervalId) !== parseInt(action.payload.intervalId)));
      Object.keys(weekSummary).map((key) => {
        weekSummary[key] = weekSummary[key].filter((x) => x.id !== action.payload.intervalId);
      });
    }

    const newInterval = action.payload;
    const newStartDate = getDateObject(newInterval, 'startDay', 'startTime');
    const newEndDate = getDateObject(newInterval, 'endDay', 'endTime');
    // check for overlapping intervals
    const isOverlap = isOverlappingIntervals(intervals, newStartDate,
      newEndDate, action.payload.intervalId);

    const daysNotCorrect = newInterval.startDay > newInterval.endDay;
    const hoursNotCorrect = newInterval.startDay === newInterval.endDay
    && newInterval.startTime > newInterval.endTime;

    if (newStartDate === newEndDate) {
      throw Error('Start and end date are equal');
    } else if (daysNotCorrect) {
      throw Error('End day is greater than start day');
    } else if (hoursNotCorrect) {
      throw Error('Start time is greater than end time');
    } else if (isOverlap) {
      throw Error('Overlap of Intervals!');
    } else {
      yield put(aAddIntervalSuccess());

      newInterval.intervalId = action.payload.intervalId || `fake#${new Date().getTime()}`;
      intervals = intervals.filter((int) => int.intervalId !== newInterval.intervalId);
      intervals.push({ ...newInterval });

      weekSummary = getNewWeekSummary(newInterval, weekSummary);

      let editedDays = yield select(getEditedDays);
      editedDays = getNewEditedDays(newInterval, editedDays);

      const filteredSummary = filteredWeekSummary(weekSummary);

      yield put(aChangeIntervalsSuccess({
        weekSummary: filteredSummary,
        intervals,
        editedDays,
      }));
    }
  } catch (error) {
    if (!error.modal) {
      yield put(aAddIntervalError({
        errorMessage: error.message,
      }));
    }
  }
}

/**
 * @memberof sagas/autobooking
 * @async
 * @param {object} action
 */
function* addTennisInterval(action) {
  try {
    const { events } = action.payload;
    if (events.length === 0) {
      throw Error('Please select one competition');
    }
    let weekSummary = {...yield select(getTennisWeekSummary)};

    let intervals = yield select(getTennisIntervals);
    // editing intervals
    if (action.payload.intervalId) {
      intervals = intervals.filter((x) => (
        parseInt(x.intervalId) !== parseInt(action.payload.intervalId)));
      Object.keys(weekSummary).map((key) => {
        weekSummary[key] = weekSummary[key].filter((x) => x.id !== action.payload.intervalId);
      });
    }

    const newInterval = action.payload;
    const newStartDate = getDateObject(newInterval, 'startDay', 'startTime');
    const newEndDate = getDateObject(newInterval, 'endDay', 'endTime');

    // check for overlapping intervals
    const isOverlap = isOverlappingIntervals(intervals, newStartDate,
      newEndDate, action.payload.intervalId);

    const daysNotCorrect = newInterval.startDay > newInterval.endDay;
    const hoursNotCorrect = newInterval.startDay === newInterval.endDay
      && newInterval.startTime > newInterval.endTime;

    if (newStartDate === newEndDate) {
      throw Error('Start and end date are equal');
    } else if (daysNotCorrect) {
      throw Error('End day is greater than start day');
    } else if (hoursNotCorrect) {
      throw Error('Start time is greater than end time');
    } else if (isOverlap) {
      throw Error('Overlap of Intervals!');
    } else {
      yield put(aAddIntervalSuccess());

      newInterval.intervalId = action.payload.intervalId || `fake#${new Date().getTime()}`;

      intervals = intervals.filter((int) => int.intervalId !== newInterval.intervalId);
      intervals.push({ ...newInterval });

      weekSummary = getNewWeekSummary(newInterval, weekSummary);

      let editedDays = yield select(getTennisEditedDays);
      editedDays = getNewEditedDays(newInterval, editedDays);

      const filteredSummary = filteredWeekSummary(weekSummary);

      yield put(aChangeTennisIntervalsSuccess({
        weekSummary: filteredSummary,
        intervals,
        editedDays,
      }));
    }
  } catch (error) {
    if (!error.modal) {
      yield put(aAddIntervalError({
        errorMessage: error.message,
      }));
    }
  }
}

/**
 * @memberof sagas/autobooking
 * @async
 * @param {object} action
 */
function* saveChanges(action) {
  let response = {};
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SET_INTERVALS,
      value: true,
    }));
    response = yield call(requestPost, API_URLS.SAVE_CHANGES, action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aSaveChangesSuccess());
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: 'Intervals are changed',
          additionalMessage: 'successfully',
          modal: SUCCESS_MODALS.INTERVALS_CHANGE,
        },
        key: FETCH_OPTIONS.SET_INTERVALS,
        value: false,
      }));
    }

    const weekSummary = filteredWeekSummary(response.data.weekSummary);
    yield put(aGetAutobookingSuccess({
      weekSummary,
      intervals: response.data.intervals,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.SET_INTERVALS,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/autobooking
 * @async
 * @param {object} action
 */
function* saveTennisChanges(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SET_TENNIS_INTERVALS,
      value: true,
    }));

    const response = yield call(requestPost, API_URLS.SAVE_TENNIS_CHANGES, action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aSaveTennisChangesSuccess());
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: 'Intervals are changed',
          additionalMessage: 'successfully',
          modal: SUCCESS_MODALS.INTERVALS_CHANGE,
        },
        key: FETCH_OPTIONS.SET_TENNIS_INTERVALS,
        value: false,
      }));
    }

    const weekSummary = filteredTennisWeekSummary(response.data.weekSummary);
    yield put(aGetTennisAutobookingSuccess({
      weekSummary,
      intervals: response.data.intervals,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.SET_TENNIS_INTERVALS,
      value: false,
    }));
  }
}
/**
 * @memberof sagas/autobooking
 * @async
 * @returns {void}
 */
export default function* autobookingSaga() {
  yield takeEvery(aGetAutoBooking, getIntervalsSaga);
  yield takeEvery(aGetTennisAutoBooking, getTennisIntervalsSaga);
  yield takeEvery(aAddInterval, addInterval);
  yield takeEvery(aAddTennisInterval, addTennisInterval);
  yield takeEvery(aSaveChanges, saveChanges);
  yield takeEvery(aSaveTennisChanges, saveTennisChanges);
}

